import React, { useRef } from 'react';
import { LazyRender } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import { BasketErrorNotification } from 'src/components/atoms/BasketErrorNotification/BasketErrorNotification';
import { PARTNER_MODULE_ID } from 'src/components/layouts/PDP/elementIds';
import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { DeliverySummary } from 'src/components/molecules/DeliverySummary/DeliverySummary';
import { useDeliverySummary } from 'src/components/molecules/DeliverySummary/useDeliverySummary';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { ProductRatingMaxiSummary } from 'src/components/molecules/ProductRatingMaxiSummary/ProductRatingMaxiSummary';
import { PublicAttributes } from 'src/components/molecules/PublicAttributes/PublicAttributes';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustCarouselWithTracking } from 'src/components/molecules/TrustCarousel/components/TrustCarouselWithTracking';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductInformation } from 'src/components/organisms/ProductInformation/components/ProductInformation';
import { ProductMediaCarousel } from 'src/components/organisms/ProductMediaCarousel/ProductMediaCarousel';
import { useProductMediaCarouselActions } from 'src/components/organisms/ProductMediaCarousel/useProductMediaCarouselActions';
import { ProductMediaModalContainer } from 'src/components/organisms/ProductMediaModal/ProductMediaModalContainer';
import { ProductOptionsContainer } from 'src/components/organisms/ProductOptions/ProductOptionsContainer';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews/containers/ProductReviewsContainer';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarousel/containers/RecommendedProductsCarouselContainer';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { trackBreadcrumbClick } from 'src/tracking/trackBreadcrumbClick';
import { trackProductInformationToggle } from 'src/tracking/trackProductInformationToggle';
import { trackProductInformationVisible } from 'src/tracking/trackProductInformationVisible';
import { trackPublicAttributesToggle } from 'src/tracking/trackPublicAttributesToggle';
import { useEditProductOptions } from '../hooks/useEditProductOptions';
import { usePDPViewModel } from '../hooks/usePDPViewModel';
import { PDPLayoutView } from './PDPLayoutView';
import * as styles from './PDPLayoutView.styles';

const DeliveryDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_drawer" */ 'src/components/organisms/DeliveryDrawer/DeliveryDrawer'
  ).then((mod) => mod.DeliveryDrawer),
);

const ProductOptionsDrawerContainer = dynamic(() =>
  import(
    /* webpackChunkName: "product_options_drawer" */ 'src/components/organisms/ProductOptions/ProductOptionsDrawerContainer'
  ).then((mod) => mod.ProductOptionsDrawerContainer),
);

const PartnerMaxiModule = dynamic(() =>
  import(
    /* webpackChunkName: "partner_maxi_module" */ 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModule'
  ).then((mod) => mod.PartnerMaxiModule),
);

const PartnerMiniModule = dynamic(() =>
  import(
    /* webpackChunkName: "partner_mini_module" */ 'src/components/molecules/PartnerMiniModule/PartnerMiniModule'
  ).then((mod) => mod.PartnerMiniModule),
);

const LAZY_LOAD_MARGIN = '200px';
const PUBLIC_ATTRIBUTES_EXPANDABLE_THRESHOLD = 6;

export const PDPLayout = () => {
  const {
    deliveryOptionsLoaded,
    hasProductOptions,
    partner,
    productFeatures,
    similarProductsServingConfig,
    viewModel,
  } = usePDPViewModel();
  const deliverySummaryProps = useDeliverySummary();
  const mediaCarouselActions = useProductMediaCarouselActions();
  const rightColumnRef = useRef<HTMLDivElement>(null);

  useEditProductOptions();

  return (
    <>
      <PDPLayoutView
        ctaColumnRef={rightColumnRef}
        ctaTrigger={
          <>
            {deliveryOptionsLoaded && <DeliverySummary {...deliverySummaryProps} />}
            <ProductOptionsContainer />
            <BasketCTAButtonContainer />
          </>
        }
        linkCarousel={<LinkCarouselContainer />}
        media={<ProductMediaCarousel {...viewModel.media} {...mediaCarouselActions} />}
        membershipBanner={<MembershipBannerSection />}
        onBreadcrumbLinkClick={trackBreadcrumbClick}
        partnerMaxiModule={
          <div id={PARTNER_MODULE_ID}>
            <LazyRender options={{ rootMargin: LAZY_LOAD_MARGIN }}>
              <PartnerMaxiModule
                dateJoined={partner.dateJoined}
                isTopBrand={partner.isTopBrand}
                location={partner.location}
                logoSrc={partner.logoSrc}
                name={partner.name}
                numberOfOrders={partner.numberOfOrders}
                onContactPartnerClick={partner.onContactPartnerClick}
                onStoryClick={partner.onStoryClick}
                onVisible={partner.onImpression}
                onVisitShopClick={partner.onVisitShopClick}
                partnerLinkHref={partner.storefrontHref}
                rating={partner.rating}
                storyImages={partner.storyImages}
                storySummary={partner.storySummary}
                storyText={partner.storyText}
              />
            </LazyRender>
          </div>
        }
        partnerMiniModule={
          <LazyRender
            options={{ rootMargin: LAZY_LOAD_MARGIN }}
            placeholder={<div css={styles.partnerMiniModulePlaceholder} />}
          >
            <PartnerMiniModule
              dateJoined={partner.dateJoined}
              isLoading={!partner.isDataLoaded}
              isTopBrand={partner.isTopBrand}
              linkHref={partner.anchorHref}
              location={partner.location}
              logoSrc={partner.logoSrc}
              name={partner.name}
              numberOfOrders={partner.numberOfOrders}
              rating={partner.rating}
            />
          </LazyRender>
        }
        partnerRecommendedProducts={
          <LazyRender options={{ rootMargin: LAZY_LOAD_MARGIN }}>
            <RecommendedProductsCarouselContainer
              servingConfig="pdp_same_seller_2"
              title="Our bestsellers"
            />
          </LazyRender>
        }
        productFeatures={productFeatures}
        productInformation={
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={trackProductInformationToggle}
          >
            <ProductInformation
              {...viewModel.productInformation}
              onVisible={trackProductInformationVisible}
            />
          </ExpandablePanel>
        }
        publicAttributes={
          viewModel.publicAttributes.length > PUBLIC_ATTRIBUTES_EXPANDABLE_THRESHOLD ? (
            <ExpandablePanel
              collapseButtonText="Read less"
              expandButtonText="Read more"
              onToggle={trackPublicAttributesToggle}
            >
              <PublicAttributes publicAttributes={viewModel.publicAttributes} />
            </ExpandablePanel>
          ) : (
            <PublicAttributes publicAttributes={viewModel.publicAttributes} />
          )
        }
        recommendedProducts={
          <LazyRender options={{ rootMargin: LAZY_LOAD_MARGIN }}>
            <RecommendedProductsCarouselContainer
              servingConfig={similarProductsServingConfig}
              title="Similar products"
            />
          </LazyRender>
        }
        reviews={
          <div id={REVIEWS_URL_FRAGMENT_ID}>
            <div css={styles.overallRatingWrapper}>
              <ProductRatingMaxiSummary
                averageRating={viewModel.productReviews.rating}
                numberOfReviews={viewModel.productReviews.count}
              />
            </div>
            <div css={styles.productReviewsWrapper}>
              <ProductReviewsContainer />
            </div>
          </div>
        }
        stickyCta={
          <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
            <BasketCTAButtonContainer />
          </StickySection>
        }
        trustCarousel={<TrustCarouselWithTracking />}
        viewModel={viewModel}
      />
      <ProductMediaModalContainer />
      <DeliveryDrawer target={rightColumnRef} />
      {hasProductOptions && <ProductOptionsDrawerContainer target={rightColumnRef} />}
      <BasketErrorNotification />
    </>
  );
};
